import { GetServerSideProps, NextPage } from "next";
import FilterLanding from "../../../components/filterLanding";
import InsightCard from "../../../components/insightCard";
import ContentGrid from "../../../components/contentGrid";
import { Previewable } from "../../../types/previewable";
import FilterProvider from "../../../components/filters/filterProvider";
import { useCallback, useState } from "react";
import { Insight } from "../../../types/insight";
import { FilterManager, FilterState } from "../../../types/filterManager";
import { setCacheControlHeader } from "../../../utils/setCacheControlHeader";
import { getLast } from "../../../utils/getLast";
import {
  getInsightsLanding,
  getInsightsLandingQuery,
  InsightsLandingData,
} from "../../../services/getInsightsLanding";
import { getInsights, InsightsData } from "../../../services/getInsights";
import { QueryableSanityData } from "../../../types/sanity";
import LiveQuery from "@sanity/preview-kit/live-query";
import HeaderNavigation from "@/components/HeaderNavigation/HeaderNavigation";
import {
  HeaderNavigationProps,
  HeaderNavigationQuery,
} from "@/components/HeaderNavigation";
import { getClient } from "lib/sanity.server";
import { isEmpty } from "@/utils/isEmpty";
import Block from "@/components/block";
import { Breadcrumbs } from "@/components/breadcrumbs";
import SEOPageHead from "@/components/SEOPageHead";
import FooterModular from "@/components/FooterModular";
import {
  FooterNavigationProps,
  FooterNavigationQuery,
} from "@/components/FooterNavigation/query";

export type InsightsLandingProps = Previewable<{
  queryableInsightsLandingData: QueryableSanityData<InsightsLandingData>;
  initialInsightsData: InsightsData;
  initialFilters: FilterState;
  navPayload: HeaderNavigationProps;
  footerPayload: FooterNavigationProps;
}>;

const insightsPerPage = 12;

const token = process.env.SANITY_API_TOKEN;

const InsightsLandingPage: NextPage<InsightsLandingProps> = (props) => {
  const insightsLandingData = props.queryableInsightsLandingData.data;

  const [insights, setInsights] = useState<Insight[]>(
    props.initialInsightsData?.insights ?? [],
  );
  const [lastInsight, setLastInsight] = useState<Insight | undefined>(
    getLast(insights),
  );
  const [numInsights, setNumInsights] = useState<number>(
    props.initialInsightsData?.numInsights ?? 0,
  );

  const loadMoreInsights = useCallback(async () => {
    const insightsResult = await getInsights({
      preview: props.preview,
      maxResults: insightsPerPage,
      lastInsight: lastInsight,
    });

    if (insightsResult.ok) {
      const moreInsights = insightsResult.content.insights;
      setLastInsight(getLast(moreInsights));
      setInsights((insights) => [...insights, ...moreInsights]);
    } else {
      // TODO handle error
    }
  }, [props.preview, setInsights, lastInsight]);

  const onFilterChange = useCallback(
    async (state: FilterState) => {
      const insightsResult = await getInsights({
        filters: state,
        preview: props.preview,
        maxResults: insightsPerPage,
      });

      if (insightsResult.ok) {
        const { insights: newInsights, numInsights } = insightsResult.content;

        setInsights(newInsights);
        setLastInsight(getLast(newInsights));
        setNumInsights(numInsights);
      } else {
        // TODO handle error
      }
    },
    [setInsights, setLastInsight, setNumInsights, props.preview],
  );

  if (insightsLandingData === null) {
    return null;
  }

  const breadcrumbs = [
    { name: "Learning", path: "/learning" },
    { name: "Adoption Blog", path: "/learning/adoption-blog" },
  ];

  return (
    <LiveQuery
      enabled={props.preview}
      query={getInsightsLandingQuery(props.preview)}
      initialData={insightsLandingData}
    >
      <>
        {insightsLandingData.seo && (
          <SEOPageHead seo={insightsLandingData.seo} />
        )}
        <HeaderNavigation navSections={props.navPayload.navSections} />
        <Block flushBottom flushTop className="mt-20 pt-5">
          <Breadcrumbs breadcrumbs={breadcrumbs} />
        </Block>
        <FilterProvider
          initialFilters={props.initialFilters}
          onFilterChange={onFilterChange}
        >
          <InsightsLanding
            onLoadMore={loadMoreInsights}
            {...insightsLandingData}
            {...props.initialInsightsData}
            numInsights={numInsights}
            insights={insights}
          />
        </FilterProvider>
        <FooterModular footerData={props.footerPayload} />
      </>
    </LiveQuery>
  );
};

type InsightsLandingInnerProps = InsightsLandingData &
  InsightsData & {
    onLoadMore: () => Promise<void>;
  };

const InsightsLanding = (props: InsightsLandingInnerProps) => {
  const { insights, numInsights, heading, subheading, onLoadMore } = props;

  return (
    <FilterLanding
      totalResults={numInsights}
      title={heading ?? "Insights"}
      description={subheading}
    >
      {insights && (
        <ContentGrid
          hideLoadMore={insights.length === numInsights}
          onLoadMore={onLoadMore}
        >
          {insights?.map((i) => <InsightCard key={i._id} {...i} />)}
        </ContentGrid>
      )}
    </FilterLanding>
  );
};

export const getServerSideProps: GetServerSideProps<
  InsightsLandingProps
> = async (context) => {
  setCacheControlHeader(context.res);

  const preview: boolean = !!context.draftMode;

  // get landing data (copy, categories, etc)
  const insightsLandingResult = await getInsightsLanding(preview);

  // if no landing data, 404
  if (!insightsLandingResult.ok) {
    return {
      notFound: true,
    };
  }
  const queryableInsightsLandingData = insightsLandingResult.content;

  // build filter state from params + landing data
  const filters: FilterManager = new FilterManager();
  for (const category of queryableInsightsLandingData.data.filterData) {
    filters.addCategoryOptions({
      items: category.options,
      categoryValue: category.value,
      categoryDisplayName: category.displayName,
      getValue: (o) => o.value,
      getDisplayName: (o) => o.displayName,
    });
  }
  const mergedFilters = filters.applyParams(context.query);
  const filterState = mergedFilters.getState();

  // get insights via filterstate
  const maxResults = insightsPerPage;
  const insightsResult = await getInsights({
    filters: filterState,
    maxResults,
    preview,
  });

  if (!insightsResult.ok) {
    return {
      notFound: true,
    };
  }
  const initialInsightsData = insightsResult.content;

  const navPayload: HeaderNavigationProps = await getClient(preview).fetch(
    HeaderNavigationQuery.query,
    {},
  );

  const footerPayload: FooterNavigationProps = await getClient(preview).fetch(
    FooterNavigationQuery.query,
    {},
  );

  if (isEmpty(navPayload) || isEmpty(footerPayload)) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      queryableInsightsLandingData,
      initialInsightsData,
      navPayload,
      footerPayload,
      initialFilters: filterState,
      preview,
      token: preview ? token : "",
    },
  };
};

export default InsightsLandingPage;
